<template>
    <v-container fluid>
        <v-row justify="center">
            <v-col cols="12">
                <responsiveButton :setText="true"
                                  setColor="orange"
                                  setDefaultIcon="mdi-refresh"
                                  setSmallIcon="mdi-refresh"
                                  :setClick="initialize"
                >
                    Refresh Data
                </responsiveButton>
                <v-divider class="mx-6"></v-divider>
                <v-data-table
                        :headers="documentRuleTable.headers"
                        :items="documentRules"
                        :loading="loading"
                        loading-text="Loading... Please wait"
                        class="elevation-1"
                        :options.sync="pagination"
                        :server-items-length="totalItems"
                >
                    <template v-slot:item.expiredInterval = "{item}">
                        <span v-if="item.expiredInterval !== DOCUMENT_RULE_CONFIGURATIONS.EXPIRATION.DEFAULT_VALUE">{{ item.expiredInterval.split('-').join(' ') }}</span>
                        <span v-else>{{DOCUMENT_RULE_CONFIGURATIONS.EXPIRATION.NO_EXPIRE_TEXT}}</span>
                    </template>
                    <template v-slot:item.required = "{item}">
                        <v-chip :color="item.required ? 'green' : 'red'" class="white-span" text-color="white">
                            {{ item.required ? 'Yes' : 'No' }}
                        </v-chip>
                    </template>
                    <template v-slot:item.approvalRequired = "{item}">
                        <v-chip :color="item.approvalRequired ? 'green' : 'red'" class="white-span" text-color="white">
                            {{ item.approvalRequired ? 'Yes' : 'No' }}
                        </v-chip>
                    </template>
                    <!--actions-->
                    <template v-slot:item.clone="{item}">
                        <v-icon
                                class="mr-2"
                                @click="cloneDocumentRule(item)"
                        >
                            {{ DOCUMENT_RULE_ACTIONS.CLONE.icon }}
                        </v-icon>
                    </template>
                    <template v-slot:item.remove="{item}">
                        <v-icon
                                class="mr-2"
                                @click="confirmDelete(item)"
                        >
                            {{ DOCUMENT_RULE_ACTIONS.DELETE.icon }}
                        </v-icon>
                    </template>
                </v-data-table>
            </v-col>
        </v-row>
        <dialog-alert
                :text="confirmAlertText"
                :alert-type="confirmAlertType"
                :open="openConfirmation"
                @alert-closed="dialogClosed($event)"
        ></dialog-alert>
    </v-container>
</template>

<script>
    import alertEventBus from '@/eventBus/AlertEventBus';
    import {DOCUMENT_RULE_CONFIGURATIONS, DOCUMENT_RULE_ACTIONS, DOCUMENT_RULE_STATUS} from '@/shared/enums/DocumentRule';
    const responsiveButton = ()=> import( '@/components/common/mobile/Button');
    const DialogAlert = () => import("@/components/common/DialogAlert");
    import { ALERT_TYPES, CONFIRM_DELETE } from "@/shared/enums/AlertTexts";

    export default {
        name: "documentRuleTable",
        components: { responsiveButton, DialogAlert },
        data(){
            return {
                documentRules: [],
                loading: true,
                documentRuleTable: {
                    headers: [
                        {
                            text: 'Name',
                            align: 'left',
                            sortable: true,
                            value: 'name'
                        },
                        {
                            text: 'Expires In',
                            align: 'center',
                            sortable: true,
                            value: 'expiredInterval'
                        },
                        {
                            text: 'Required',
                            align: 'center',
                            sortable: true,
                            value: 'required'
                        },
                        {
                            text: 'Requires Approval',
                            align: 'center',
                            sortable: true,
                            divider: true,
                            value: 'approvalRequired'
                        },
                        {
                            text: 'Clone',
                            align: 'center',
                            width: '2%',
                            value: 'clone',
                            sortable: false
                        },
                        {
                            text: 'Remove',
                            align: 'center',
                            width: '2%',
                            value: 'remove',
                            sortable: false
                        }
                    ]
                },
                confirmAlertType: ALERT_TYPES.CONFIRM,
                confirmAlertText: CONFIRM_DELETE,
                openConfirmation: false,
                documentRuleToDelete: null,
                //pagination properties
                totalItems: 0,
                pagination: {},
                pageKeyCache: new Map()
            };
        },
        watch: {
            pagination: {
                async handler () {
                    await this.getDocumentRules();
                },
                deep: true
            }
        },
        computed:{
            DOCUMENT_RULE_CONFIGURATIONS(){
                return DOCUMENT_RULE_CONFIGURATIONS;
            },
            DOCUMENT_RULE_ACTIONS(){
                return DOCUMENT_RULE_ACTIONS;
            }
        },
        methods:{
            initialize() {
                this.getDocumentRules();
            },
            async getDocumentRules() {
                this.loading = true;
                let params = this.getPaginationValues();
                if(!params.limit)
                    return;

                //going backend
                try{
                    await this.$store.dispatch('getDocumentRules', params);
                    this.documentRules = this.$store.getters.getDocumentRules;
                }
                catch (e) {
                    //set alert
                    this.loading = false;
                    alertEventBus.setAlert({type:'error', msg: e.message});
                    return;
                }

                this.setPaginationValues(this.documentRules);
                this.loading = false;
            },
            getPaginationValues(){
                //all values of pagination that can be used for backend
                const { page, itemsPerPage } = this.pagination;
                let params = {
                    limit: itemsPerPage
                }

                if(this.pageKeyCache.has(page) && page != 1){
                    params.lastEvaluatedKey = this.pageKeyCache.get(page);
                }
                else {
                    //we have not been there yet, so it needs to be to cases 1: this is brand new search page 0
                    // or it is the next page that we has not seem
                    if(page != 1) {
                        params.lastEvaluatedKey = this.pageKeyCache.get(page-1);
                    }
                }
                return params;
            },
            setPaginationValues(list){
                let key = this.$store.getters.getDocumentsPageKey;
                const {page} = this.pagination;

                if(key != null && Object.keys(key).length > 0){
                    this.pageKeyCache.set(page, key);
                    this.totalItems = list.length +1;
                }
                else{
                    this.totalItems = list.length;
                }
            },
            async cloneDocumentRule(documentRule){
                this.$store.commit('setDocumentRule',{...documentRule})
                this.$router.push({name:'documentRuleClone', params: {id: documentRule.id}});
            },
            async deleteDocumentRule(documentRule) {
                const index = this.documentRules.indexOf(documentRule);
                documentRule.status = DOCUMENT_RULE_STATUS.INACTIVE;
                await this.$store.dispatch("setDocumentRule", documentRule);
                let response = this.$store.getters.getDocumentRule;

                this.documentRules.splice(index, 1);
                this.documentRuleToDelete = null;
            },
            confirmDelete(item) {
                this.openConfirmation = true;
                this.documentRuleToDelete = item;
            },
            dialogClosed(answer) {
                this.openConfirmation = false;
                if (answer) {
                    this.deleteDocumentRule(this.documentRuleToDelete);
                }
            }
        },
        async created(){
            this.initialize();
        }
    }
</script>

<style>
    .v-chip.white-span span {
        color: white !important;
    }
</style>